// ============================================================================
// CRM TOTALS — header figures for the Studio CRM screen
// Pass in the links visible under the current filters (booking-level and
// opp-level), so the header always agrees with the rows. Those links are then
// de-duplicated by opportunityId: one opp linked to several visible bookings
// counts once, using its most recently updated link's values, and counts as
// sourced if any visible link says so. Money is summed per currency, never
// converted. Cross-booking conflicts are a data-quality flag and are computed
// separately over every link (findConflicts), independent of filters.
// ============================================================================

import {
  BookingOpportunityLink,
  CrmAttribution,
  CrmCurrency,
  effectiveValues
} from '../../services/studio-crm';

export type CurrencyTotals = Partial<Record<CrmCurrency, number>>;
export type AttributionSplit = Record<CrmAttribution, { count: number; value: CurrencyTotals }>;

export interface CrmTotals {
  opportunityCount: number;
  openPipeline: CurrencyTotals;
  wonTcv: CurrencyTotals;
  wonCount: number;
  lostCount: number;
  openCount: number;
  winRate: number | null;   // won ÷ (won + lost); null when nothing has closed
  pipelineSplit: AttributionSplit;  // open opps by sourced / influenced
  wonSplit: AttributionSplit;       // won opps by sourced / influenced
  conflictingIds: Set<string>;  // same opp linked with different values or attribution on different bookings
}

function add(totals: CurrencyTotals, currency: CrmCurrency, value: number) {
  totals[currency] = (totals[currency] || 0) + value;
}

function valueKey(link: BookingOpportunityLink): string {
  const v = effectiveValues(link);
  return `${v.state}|${v.value}|${v.currency}|${link.attribution}`;
}

export function dedupeOpportunities(links: BookingOpportunityLink[]): {
  unique: BookingOpportunityLink[];
  conflictingIds: Set<string>;
} {
  const latest = new Map<string, BookingOpportunityLink>();
  const keys = new Map<string, Set<string>>();
  const sourced = new Set<string>();
  for (const link of links) {
    if (link.attribution === 'sourced') sourced.add(link.opportunityId);
    const prev = latest.get(link.opportunityId);
    if (!prev || link.updatedAt > prev.updatedAt) latest.set(link.opportunityId, link);
    const set = keys.get(link.opportunityId) || new Set<string>();
    set.add(valueKey(link));
    keys.set(link.opportunityId, set);
  }
  const conflictingIds = new Set(Array.from(keys.entries()).filter(([, s]) => s.size > 1).map(([id]) => id));
  const unique = Array.from(latest.values()).map(link =>
    sourced.has(link.opportunityId) && link.attribution !== 'sourced' ? { ...link, attribution: 'sourced' as const } : link
  );
  return { unique, conflictingIds };
}

export function findConflicts(allLinks: BookingOpportunityLink[]): Set<string> {
  return dedupeOpportunities(allLinks).conflictingIds;
}

function emptySplit(): AttributionSplit {
  return { sourced: { count: 0, value: {} }, influenced: { count: 0, value: {} } };
}

// Converts per-currency totals to EUR with the admin-set reporting rates
// (EUR per 1 unit). Returns null if any currency present has no rate, so a
// partial conversion is never shown as a total.
export function toEur(totals: CurrencyTotals, rates: Partial<Record<CrmCurrency, number>> | null): number | null {
  let sum = 0;
  for (const [currency, value] of Object.entries(totals) as [CrmCurrency, number][]) {
    if (!value) continue;
    const rate = currency === 'EUR' ? 1 : rates?.[currency];
    if (!rate) return null;
    sum += value * rate;
  }
  return sum;
}

export function computeTotals(links: BookingOpportunityLink[]): CrmTotals {
  const { unique, conflictingIds } = dedupeOpportunities(links);
  const totals: CrmTotals = {
    opportunityCount: unique.length,
    openPipeline: {},
    wonTcv: {},
    wonCount: 0,
    lostCount: 0,
    openCount: 0,
    winRate: null,
    pipelineSplit: emptySplit(),
    wonSplit: emptySplit(),
    conflictingIds
  };

  for (const link of unique) {
    const v = effectiveValues(link);
    if (v.state === 'open') {
      add(totals.openPipeline, v.currency, v.value);
      totals.openCount++;
      totals.pipelineSplit[link.attribution].count++;
      add(totals.pipelineSplit[link.attribution].value, v.currency, v.value);
    }
    if (v.state === 'won') {
      add(totals.wonTcv, v.currency, v.value);
      totals.wonCount++;
      totals.wonSplit[link.attribution].count++;
      add(totals.wonSplit[link.attribution].value, v.currency, v.value);
    }
    if (v.state === 'lost') totals.lostCount++;
  }

  const closed = totals.wonCount + totals.lostCount;
  totals.winRate = closed > 0 ? totals.wonCount / closed : null;
  return totals;
}

// Per-booking figures (no cross-booking de-duplication needed within one booking)
export function bookingFigures(links: BookingOpportunityLink[]): { pipeline: CurrencyTotals; won: CurrencyTotals } {
  const pipeline: CurrencyTotals = {};
  const won: CurrencyTotals = {};
  for (const link of links) {
    const v = effectiveValues(link);
    if (v.state === 'open') add(pipeline, v.currency, v.value);
    if (v.state === 'won') add(won, v.currency, v.value);
  }
  return { pipeline, won };
}

export function formatMoney(value: number, currency: CrmCurrency, compact = false): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: compact ? 1 : 0,
    notation: compact ? 'compact' : 'standard'
  }).format(value);
}

export function formatCurrencyTotals(totals: CurrencyTotals, compact = true): string {
  const parts = (Object.entries(totals) as [CrmCurrency, number][])
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([c, v]) => formatMoney(v, c, compact));
  return parts.length ? parts.join(' · ') : '—';
}
